import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { Newspaper, ServerCrash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'
import { EditNewsContentForm } from '../../schemas/EditNewsContentSchema'
import { EditNewsImageForm } from '../../schemas/EditNewsImageSchema'
import { NewsCropForm } from '../../schemas/NewsImageSchema'
import { NewsSummaryForm } from '../../schemas/NewsSummarySchema'
import { useEditNewsStore } from '../stores/useEditNewsStore'

async function postNews(id: string, data: FormData) {
  return api.backend.put(`/posts/${id}`, data)
}

type Form = NewsSummaryForm &
  EditNewsContentForm &
  EditNewsImageForm &
  NewsCropForm

export function useEditNews() {
  const { t } = useTranslation('panel')
  const { news } = useEditNewsStore()
  const { mutateAsync, isPending } = useMutation<
    unknown,
    AxiosError<ApiError>,
    { id: string; data: FormData }
  >({
    mutationFn: (variables) => postNews(variables.id, variables.data),
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: Newspaper,
          title: t('news.use_edit_news.toast_success.title'),
        },
      })
      if (news?.PostsPinned?.length) {
        queryClient.invalidateQueries({ queryKey: ['/posts/paginate/pinned'] })
      }
      if (news?.isSlider) {
        queryClient.invalidateQueries({ queryKey: ['/posts-slider/paginate'] })
      } else {
        queryClient.invalidateQueries({ queryKey: ['/posts/paginate'] })
      }
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t('news.use_edit_news.toast_error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('news.use_edit_news.toast_error.description'),
        },
      })
    },
  })

  const handleEditNews = async (id: string, form: Form) => {
    const data = new FormData()

    if (form.postImage instanceof File) data.append('postImage', form.postImage)
    if (form.postFile instanceof File) data.append('postFile', form.postFile)

    const body = {
      title: form.title,
      subTitle: form.subtitle,
      post: {
        content: form.content,
      },
      postExternalUrl: form.postExternalUrl,
      tag: form.tag?.map((tag) => tag.value) || [],
      postImage: form.postImage,
      postFile: news?.postFile,
      isSlider: form.isSlider,
    }

    if (form.postFile === null) Object.assign(body, { postFile: null })

    data.append('body', JSON.stringify(body))

    await mutateAsync({ id, data })
  }

  return {
    handleEditNews,
    isEditingNews: isPending,
  }
}
